import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

/* ============================================================
   SYSTEM BOOT — Bright white sci-fi command terminal
   Typed: INITIALIZING / THE COMMAND / WELCOME, COMMANDER + glitch
   ============================================================ */

const RED = '#FF8618';
const NAVY = '#f2f4f6';
const STEEL = '#a8b0b8';
const ICE = '#3A8DDE';

const SMALL1 = 'INITIALIZING';
const SMALL2 = 'THE COMMAND';
const BIG = 'WELCOME, COMMANDER';

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const Logo = () => (
  <div className="flex items-center gap-3" data-testid="boot-logo">
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
      <path d="M17 2 L31 9 L31 25 L17 32 L3 25 L3 9 Z" stroke={RED} strokeWidth="1.4" fill="rgba(255,134,24,0.06)" />
      <path d="M17 8 L25 12.5 L25 21.5 L17 26 L9 21.5 L9 12.5 Z" fill={RED} opacity="0.9" />
      <path d="M17 13 L17 26" stroke="#fff" strokeWidth="1.2" />
    </svg>
    <span style={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 900, fontSize: '18px', letterSpacing: '0.14em', color: NAVY }}>
      GAL WAR <span style={{ color: RED }}>CC</span>
    </span>
  </div>
);

const Caret = () => <span className="boot-caret" style={{ color: RED, fontWeight: 400 }}>_</span>;

const BootSequence = ({ onComplete }) => {
  const [s1, setS1] = useState('');
  const [s2, setS2] = useState('');
  const [big, setBig] = useState('');
  const [active, setActive] = useState('s1'); // s1 | s2 | big | glitch | done
  const [panels, setPanels] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const type = (text, setter, speed) =>
      new Promise((res) => {
        let i = 0;
        const id = setInterval(() => {
          if (cancelled) { clearInterval(id); return; }
          i += 1;
          setter(text.slice(0, i));
          if (i >= text.length) { clearInterval(id); res(); }
        }, speed);
      });

    (async () => {
      await wait(350);
      setPanels(true);
      setActive('s1');
      await type(SMALL1, setS1, 60);
      await wait(220);
      setActive('s2');
      await type(SMALL2, setS2, 60);
      await wait(280);
      setActive('big');
      await type(BIG, setBig, 55);
      await wait(180);
      if (cancelled) return;
      setActive('glitch');
      await wait(950);
      if (cancelled) return;
      setActive('done');
      await wait(750);
      if (!cancelled && onComplete) onComplete();
    })();

    return () => { cancelled = true; };
  }, [onComplete]);

  const skip = () => onComplete && onComplete();

  return (
    <div
      className="fixed inset-0 z-[100] overflow-hidden cursor-pointer"
      style={{ background: 'radial-gradient(circle at 50% 40%, #1B222A 0%, #10161C 50%, #0A0F14 100%)' }}
      data-testid="boot-sequence"
      onClick={skip}
    >
      {/* circuit grid */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(58,141,222,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(58,141,222,0.10) 1px, transparent 1px)',
        backgroundSize: '46px 46px',
        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 88%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 88%)',
      }} />
      {/* fine circuit dots */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(rgba(58,141,222,0.14) 1px, transparent 1px)',
        backgroundSize: '14px 14px', opacity: 0.5,
      }} />
      {/* center command-core pyramid */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg width="640" height="640" viewBox="0 0 640 640" className="boot-core-spin" style={{ opacity: 0.5 }}>
          <polygon points="320,90 540,470 100,470" fill="none" stroke="rgba(58,141,222,0.28)" strokeWidth="1.2" />
          <polygon points="320,170 470,440 170,440" fill="none" stroke="rgba(255,134,24,0.18)" strokeWidth="1" />
          <circle cx="320" cy="360" r="250" fill="none" stroke="rgba(58,141,222,0.14)" strokeWidth="1" strokeDasharray="3 8" />
          <circle cx="320" cy="360" r="180" fill="none" stroke="rgba(255,134,24,0.12)" strokeWidth="1" strokeDasharray="2 10" />
        </svg>
      </div>
      {/* scanlines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'repeating-linear-gradient(0deg, rgba(58,141,222,0.035) 0px, rgba(58,141,222,0.035) 1px, transparent 2px, transparent 5px)',
      }} />
      {/* corner frame markers */}
      {['top-5 left-5 border-t-2 border-l-2', 'top-5 right-5 border-t-2 border-r-2', 'bottom-5 left-5 border-b-2 border-l-2', 'bottom-5 right-5 border-b-2 border-r-2'].map((c, i) => (
        <div key={i} className={`absolute w-7 h-7 ${c} pointer-events-none`} style={{ borderColor: 'rgba(255,134,24,0.55)' }} />
      ))}
      {/* faint crosshair marks */}
      <div className="absolute left-1/2 top-[16%] -translate-x-1/2 pointer-events-none" style={{ opacity: 0.5 }}>
        <svg width="26" height="26" viewBox="0 0 26 26"><path d="M13 2v8M13 16v8M2 13h8M16 13h8" stroke={RED} strokeWidth="1" /><circle cx="13" cy="13" r="3" fill="none" stroke={RED} strokeWidth="1" /></svg>
      </div>

      {/* ===== LOGO (top-left) ===== */}
      <div className="absolute top-6 left-6 sm:top-8 sm:left-10 z-20"><Logo /></div>

      {/* ===== TOP-RIGHT: DATA STREAM ===== */}
      <div className={`boot-panel absolute top-6 right-6 sm:top-8 sm:right-10 z-20 ${panels ? 'is-in' : ''}`} data-testid="boot-data-panel"
        style={{ width: '210px', background: 'rgba(16,22,28,0.7)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', padding: '14px 16px' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: RED, boxShadow: `0 0 6px ${RED}` }} />
          <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '11px', letterSpacing: '0.26em', color: NAVY }}>DATA STREAM</span>
        </div>
        {['SECTOR 77A', 'SECTOR 52A', 'SECTOR 52Z', 'SECTOR 91B'].map((s) => (
          <div key={s} className="flex items-center justify-between py-0.5">
            <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 600, fontSize: '11px', letterSpacing: '0.12em', color: STEEL }}>{s}</span>
            <span style={{ fontSize: '9px', color: ICE, letterSpacing: '0.1em' }}>●</span>
          </div>
        ))}
        <div className="mt-3 mb-1" style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.2em', color: RED }}>DATA PACKET</div>
        <div className="flex items-end gap-[3px] h-8">
          {[5, 9, 4, 7, 3, 8, 6, 10, 4, 7, 5, 9].map((h, i) => (
            <span key={i} className="boot-bar flex-1" style={{ height: `${h * 10}%`, background: i % 3 === 0 ? RED : ICE, opacity: 0.7, animationDelay: `${i * 0.12}s` }} />
          ))}
        </div>
      </div>

      {/* ===== CENTER TEXT ===== */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center z-10 pointer-events-none">
        <div className="mb-5 leading-tight" data-testid="boot-init-text">
          <p style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: 'clamp(0.9rem,1.6vw,1.15rem)', letterSpacing: '0.4em', color: STEEL }}>
            {s1}{active === 's1' && <Caret />}
          </p>
          <p style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: 'clamp(0.9rem,1.6vw,1.15rem)', letterSpacing: '0.4em', color: STEEL }}>
            {s2}{active === 's2' && <Caret />}
          </p>
        </div>
        <h1
          data-testid="boot-welcome-text"
          style={{
            fontFamily: '"Orbitron", sans-serif', fontWeight: 900,
            fontSize: 'clamp(2.2rem, 7vw, 6rem)', letterSpacing: '0.02em', color: NAVY,
            textShadow: '0 0 18px rgba(245,247,248,0.4), 0 0 42px rgba(200,205,210,0.18), 0 2px 0 rgba(0,0,0,0.5)',
            lineHeight: 1,
          }}
        >
          {big}{active === 'big' && <Caret />}
        </h1>
        {/* center emblem below */}
        <div className="mt-7 flex items-center gap-3" style={{ opacity: active === 'done' || active === 'glitch' ? 1 : 0, transition: 'opacity 0.5s' }}>
          <span className="block h-px w-16" style={{ background: `linear-gradient(90deg, transparent, ${RED})` }} />
          <svg width="22" height="22" viewBox="0 0 22 22"><path d="M11 1 L21 6 L21 16 L11 21 L1 16 L1 6 Z" fill="none" stroke={RED} strokeWidth="1.2" /><circle cx="11" cy="11" r="3" fill={RED} /></svg>
          <span className="block h-px w-16" style={{ background: `linear-gradient(90deg, ${RED}, transparent)` }} />
        </div>
      </div>

      {/* ===== BOTTOM-LEFT: SYSTEM CHECK ===== */}
      <div className={`boot-panel absolute bottom-6 left-6 sm:bottom-8 sm:left-10 z-20 ${panels ? 'is-in' : ''}`} data-testid="boot-system-panel"
        style={{ width: '256px', background: 'rgba(16,22,28,0.7)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', padding: '16px 18px', transitionDelay: '0.1s' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: RED, boxShadow: `0 0 6px ${RED}` }} />
          <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '11px', letterSpacing: '0.26em', color: NAVY }}>SYSTEM CHECK</span>
        </div>
        {[['CORE MODULES', 'OK'], ['SECURITY PROTOCOL', 'OK'], ['NETWORK LINK', 'ESTABLISHED'], ['COMMAND INTERFACE', 'OK']].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-[3px]">
            <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 600, fontSize: '11px', letterSpacing: '0.08em', color: STEEL }}>{k}</span>
            <span className="flex items-center gap-1" style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.1em', color: '#1F9D55' }}>
              <Check className="w-3 h-3" /> {v}
            </span>
          </div>
        ))}
        <div className="mt-3 pt-2 flex items-center gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <span className="w-2 h-2 rounded-full boot-bar" style={{ background: '#1F9D55', boxShadow: '0 0 8px #1F9D55' }} />
          <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '11px', letterSpacing: '0.16em', color: NAVY }}>ALL SYSTEMS OPERATIONAL</span>
        </div>
      </div>

      {/* ===== BOTTOM-RIGHT: ACCESS ===== */}
      <div className={`boot-panel absolute bottom-6 right-6 sm:bottom-8 sm:right-10 z-20 text-right ${panels ? 'is-in' : ''}`} data-testid="boot-access-panel"
        style={{ width: '210px', background: 'rgba(16,22,28,0.7)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', padding: '14px 16px', transitionDelay: '0.18s' }}>
        <div style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', color: NAVY }}>35.6895° N</div>
        <div style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', color: NAVY }}>139.6917° E</div>
        <div className="mt-3" style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.16em', color: RED }}>ACCESS LEVEL: COMMANDER</div>
        <div className="mt-2 flex gap-[3px] justify-end">
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className="block h-2 flex-1" style={{ background: i < 8 ? RED : 'rgba(255,255,255,0.14)', boxShadow: i < 8 ? '0 0 5px rgba(255,134,24,0.6)' : 'none' }} />
          ))}
        </div>
      </div>

      {/* skip hint */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20" style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 600, fontSize: '10px', letterSpacing: '0.3em', color: STEEL, opacity: 0.7 }}>
        CLICK TO ENTER
      </div>
    </div>
  );
};

export default BootSequence;
